import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

import { PlayerRankingReportContainer } from './player-ranking-report.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SportTerminology } from '../../../lib/types';
import { REPORT_CARD_ACTIONS_FIELDS } from '../../../lib/queries/report-card-access.queries';


import eastmanPreview from '../../../assets/eastman-conclude-template.png';
import generalConcludePreview from '../../../assets/conclude-template-general.png';
import { IConcludePlayerOptions } from '../types/conclude-player-options.types';

const PLAYER_ASSESSMENT_RANKING_FIELDS = gql`
  fragment PlayerAssessmentRankingFields on PlayerAssessment {
    _id
    level
    team
    attendanceCount
    assessmentSessionCount
    attendanceCounts {
      attending
      pending
      notAttending
      total
    }
    position {
      _id
      name
    }
    adjustedScore
    isConcluded
    concludedAt
    isHeld
    deletedAt
    unappliedActionsCount
    contactReach
    positionRank
    overallRank
    ...ReportCardActionsFields
  }

  ${REPORT_CARD_ACTIONS_FIELDS}
`;

const GET_PLAYER_RANKINGS = gql`
  query GetPlayerRankings($assessmentId: ID!, $ageGroupId: ID!) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: [$ageGroupId], includeArchived: true) {
      ...PlayerAssessmentRankingFields
    }
  }

  ${PLAYER_ASSESSMENT_RANKING_FIELDS}
`;

// These three pair the active assessment with the routed age group. reports.page.tsx redirects HOME when
// that age group isn't in the active assessment; without that guard they write against a mismatched pair.
const ASSIGN_LEVEL = gql`
  mutation AssignLevel($assessmentId: ID!, $ageGroupId: ID!, $playerAssessmentIds: [ID!]!, $level: String!) {
    assignLevel(assessmentId: $assessmentId, ageGroupId: $ageGroupId, playerAssessmentIds: $playerAssessmentIds, level: $level) {
      _id
      level
    }
  }
`;

const ASSIGN_TEAM = gql`
  mutation AssignTeam($assessmentId: ID!, $ageGroupId: ID!, $playerAssessmentIds: [ID!]!, $team: String!) {
    assignTeam(assessmentId: $assessmentId, ageGroupId: $ageGroupId, playerAssessmentIds: $playerAssessmentIds, team: $team) {
      _id
      team
    }
  }
`;

const CONCLUDE_PLAYERS = gql`
  mutation ConcludePlayers($ageGroupId: ID!, $assessmentId: ID!, $playerAssessmentIds: [ID!]!, $sendNotifications: Boolean!, $concludeEmailTemplate: ConcludeEmailTemplate!) {
    concludePlayers(ageGroupId: $ageGroupId, assessmentId: $assessmentId, playerAssessmentIds: $playerAssessmentIds, sendNotifications: $sendNotifications, concludeEmailTemplate: $concludeEmailTemplate) {
      ...PlayerAssessmentRankingFields
    }
  }

  ${PLAYER_ASSESSMENT_RANKING_FIELDS}
`;

interface IPlayerRankingReportGraphQL {
  ageGroup: IAgeGroup;
  goalieFilter: 'goalie' | 'other';
}


const concludeEmailTemplateOptions: IConcludePlayerOptions[] = [
  {
    label: 'Minor Hockey - Concluded Template',
    value: 'CONCLUDE',
    subjectLine: `{{trigger.firstName}}'s recent hockey assessment with the {{trigger.associationName}} has now concluded.`,
    preview: generalConcludePreview
  },
  {
    label: 'AAA/AA - Release Template',
    value: 'CONCLUDE_AA_PLUS',
    subjectLine: `{{trigger.playerName}}'s Evaluation has Concluded`,
    preview: eastmanPreview
  }
]

export const PlayerRankingReportGraphQL: React.FC<IPlayerRankingReportGraphQL> = ({ ageGroup, goalieFilter }) => {
  const termLookup = useCompanySportTerminology();
  const athletesTerm = pluralTerm(termLookup[SportTerminology.ATHLETE].toLowerCase());

  const user = getAuthUser();
  const [assignLevelMutation] = useMutation(ASSIGN_LEVEL);
  const [assignTeamMutation] = useMutation(ASSIGN_TEAM);
  const [concludePlayersMutation] = useMutation(CONCLUDE_PLAYERS);
  const { data, error, loading, refetch } = useQuery(GET_PLAYER_RANKINGS, {
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      ageGroupId: ageGroup._id
    },
    fetchPolicy: 'cache-and-network'
  });

  const playerAssessments = (data && data.playerAssessments.filter((p: IPlayerAssessment) => (!p.deletedAt || p.adjustedScore !== "-") && !!p._id)) || [];

  const handleAssignLevel = (playerAssessmentIds: string[], level: string) => {
    if (window.confirm(`Are you sure you want to assign a level to ${playerAssessmentIds.length} ${athletesTerm}?`)) {
      assignLevelMutation({
        variables: {
          playerAssessmentIds,
          level,
          assessmentId: user?.settings.activeAssessmentId,
          ageGroupId: ageGroup._id
        },
      });
    }
  }

  const handleAssignTeam = (playerAssessmentIds: string[], team: string) => {
    if (window.confirm(`Are you sure you want to assign a team to ${playerAssessmentIds.length} ${athletesTerm}?`)) {
      assignTeamMutation({
        variables: {
          playerAssessmentIds,
          team,
          assessmentId: user?.settings.activeAssessmentId,
          ageGroupId: ageGroup._id
        },
      });
    }
  }

  const handleConcludePlayers = (playerAssessmentIds: string[], sendNotifications: boolean, concludeEmailTemplate: string) => {
    const emailTemplate = concludeEmailTemplateOptions.find(option => option.value === concludeEmailTemplate)?.label;
    if (window.confirm(`Are you sure you want to conclude ${playerAssessmentIds.length} players? ${sendNotifications ? `Notifications will be sent, with template: ${emailTemplate}.` : 'Notifications will not be sent.'}`)) {
      concludePlayersMutation({
        variables: {
          playerAssessmentIds,
          sendNotifications,
          concludeEmailTemplate,
          assessmentId: user?.settings.activeAssessmentId,
          ageGroupId: ageGroup._id
        },
        update: (cache, { data }) => {
          // Update the assessment session cache with the concluded players immediately, or refetch query if no data is returned
          if (data?.concludePlayers.length) {
            const assessmentSessionId = data.concludePlayers[0].assessmentSessionId;
            cache.modify({
              id: `AssessmentSession:${assessmentSessionId}`,
              fields: {
                playerAssessments: () => data.concludePlayers
              }
            });
          } else {
            refetch();
          }
        }
      })
    }
  }

  return <PlayerRankingReportContainer
    ageGroup={ageGroup}
    playerAssessments={playerAssessments}
    loading={loading}
    fetchError={Boolean(error)}
    assignLevel={handleAssignLevel}
    assignTeam={handleAssignTeam}
    concludePlayers={handleConcludePlayers}
    concludeEmailTemplateOptions={concludeEmailTemplateOptions}
    goalieFilter={goalieFilter}
  />;
};
